/**
 * Deterministic value noise for generated layouts.
 *
 * A layout must be the same on the server, on every client and in every
 * build, so nothing here touches Math.random: every value is a pure function
 * of its coordinates and a seed. Value noise (a hashed lattice, smoothly
 * interpolated) is enough to shape biomes and groves; Perlin's gradients
 * would buy nothing visible at 16 pixels a tile.
 */
/** A fixed hash of a lattice point, in [0, 1). */
export function hashNoise(x, y, seed) {
    let h = (x * 374761393 + y * 668265263 + seed * 2246822519) ^ 0x5bd1e995;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function smoothstep(t) {
    return t * t * (3 - 2 * t);
}
/** Value noise at a real point, in [0, 1): lattice hashes blended with a smoothstep. */
export function valueNoise(x, y, seed) {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const tx = smoothstep(x - x0);
    const ty = smoothstep(y - y0);
    const top = lerp(hashNoise(x0, y0, seed), hashNoise(x0 + 1, y0, seed), tx);
    const bottom = lerp(hashNoise(x0, y0 + 1, seed), hashNoise(x0 + 1, y0 + 1, seed), tx);
    return lerp(top, bottom, ty);
}
function lerp(a, b, t) {
    return a + (b - a) * t;
}
/**
 * Fractal noise: octaves of value noise summed, each twice as fine and
 * `persistence` times as strong. Normalised to [0, 1], centred near 0.5.
 */
export function fbm(x, y, options) {
    const octaves = options.octaves ?? 3;
    const persistence = options.persistence ?? 0.5;
    let amplitude = 1;
    let frequency = 1 / options.scale;
    let total = 0;
    let range = 0;
    for (let i = 0; i < octaves; i += 1) {
        total += amplitude * valueNoise(x * frequency, y * frequency, options.seed + i * 101);
        range += amplitude;
        amplitude *= persistence;
        frequency *= 2;
    }
    return total / range;
}
//# sourceMappingURL=noise.js.map